'use client';

import { useEffect } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { useSecureAuthContext } from '../auth/services';
import { useGetUserProfileQuery } from '@/api/features/users';
import { useAppDispatch } from '../store/hooks';
import { setUserSession } from '../store/slices/userProfileSlice';

/**
 * HYBRID PATTERN: UserProfileLoader
 * 
 * Enhances RTK Query user profile data with computed UI fields and session management.
 * No longer duplicates server data - RTK Query remains the single source of truth.
 * 
 * Purpose:
 * - Provides app-wide session cache with computed fields (fullName, initials, avatar)
 * - Bridges RTK Query data with Redux session management
 * - Enables components to access enhanced user data without prop drilling
 */
export function UserProfileLoader() {
  const { isAuthenticated } = useSecureAuthContext();
  const dispatch = useAppDispatch();
  
  // Skip during SSR/prerendering
  const isSSR = typeof window === 'undefined';
  
  // RTK QUERY: Single source of truth for server data
  const { data: userProfile, isLoading, isError, error, fulfilledTimeStamp } = useGetUserProfileQuery(undefined, {
    skip: !isAuthenticated || isSSR,
  });
  
  // HYBRID ENHANCEMENT: Create session cache with computed UI fields
  // Listen to RTK Query cache changes (including from mutations)
  useEffect(() => {
    // Skip during SSR
    if (isSSR) return;
    
    if (userProfile && !isLoading) {
      reduxLogger.info('[UserProfileLoader] Syncing RTK Query data to session cache:', {
        timestamp: fulfilledTimeStamp,
        profile: userProfile
      });
      
      // ENHANCEMENT: Add computed fields to RTK Query data
      // This creates a session cache without duplicating server data
      // Updates automatically when RTK Query cache changes (from mutations)
      dispatch(setUserSession(userProfile));
    } else if (!isAuthenticated) {
      // CLEANUP: Clear session on logout
      dispatch(setUserSession(null));
    }
  }, [userProfile, isLoading, isAuthenticated, dispatch, fulfilledTimeStamp, isSSR]);

  // Avoid surfacing transient errors if we have any cached session
  useEffect(() => {
    if (isError && !isSSR) {
      // If we already have a session in Redux, ignore transient fetch errors
      // UI should continue rendering with cached session
      reduxLogger.warn('[UserProfileLoader] Ignoring transient profile fetch error if session exists', { error });
    }
  }, [isError, error, isSSR]);
  
  // ERROR HANDLING: Log errors for debugging
  useEffect(() => {
    // Skip during SSR
    if (isSSR) return;
    
    if (isError) {
      reduxLogger.error('[UserProfileLoader] Error loading user profile:', error);
    }
  }, [isError, error, isSSR]);
  
  // This component doesn't render anything - it's a data enhancement provider
  return null;
}
